import { FC, useCallback, useState } from 'react';
import { useAtom } from 'jotai';
import { Accordion, AccordionItem } from '@carbon/react';
import {
  WELLBORE_GEOMETRY_ATTRIBUTE,
  WORKSPACE_COLOR_TARGETS,
  WellsColorBySection,
  resolveWellColor,
  workspaceColorRulesAtom,
} from 'components/wells-color-by';
import {
  LayoutSettings,
  RailSectionTitle,
  isCardVisible,
} from 'components/workspace-cards';
import type { StagedWell } from 'types/well-workspace';
import { DraggableDivider } from 'components/draggable-divider';
import { WellTree } from './well-tree';
import { DETAIL_CARDS } from './section-cards';
import {
  WORKSPACE_COLOR_ATTRIBUTES,
  workspaceColorOptions,
} from './color-by-wells';
import styles from './workspace-rail.module.scss';

interface Props {
  wells: StagedWell[];
  selectedUwi: string | null;
  onSelectWell: (uwi: string) => void;
  visibleCards: Record<string, boolean>;
  onToggleCard: (cardId: string, visible: boolean) => void;
  /**
   * Opens the Add-or-remove-wells modal. Omitted while there is no membership
   * endpoint — the control then shows, disabled.
   */
  onManageWells?: () => void;
  /** Why that control is offered but unavailable. See WellTree. */
  manageWellsHint?: string;
}

/**
 * The workspace's left rail: the well picker above a draggable splitter, and below it
 * the collapsible control sections — map filter, colour-by rules, layout settings, and
 * the Subsurface Explorer's own filters.
 *
 * Four accordion items open by default, as the mock has it. Each owns its open state
 * rather than leaving it to Carbon, the way the cross-section's settings panel does, so
 * a section can be opened programmatically later.
 * @param {Props} props The wells, the selection, the card toggles and the portal host.
 * @return {JSX.Element} The rail.
 */
export const WorkspaceRail: FC<Props> = ({
  wells,
  selectedUwi,
  onSelectWell,
  visibleCards,
  onToggleCard,
  onManageWells,
  manageWellsHint,
}) => {
  // Percent of the rail's height given to the well tree. The mock puts the handle just
  // under halfway, leaving the tree the larger pane.
  const [treeSplitPercent, setTreeSplitPercent] = useState(45);

  const [open, setOpen] = useState({
    wells: true,
    layout: true,
    subsurface: true,
  });
  const toggle = (key: keyof typeof open) =>
    setOpen((prev) => ({ ...prev, [key]: !prev[key] }));

  // Workspace-scoped colour rules, in their own atom rather than map-query's so the two
  // surfaces do not fight over one rule list — and so the Map card, a sibling of this
  // rail, can read what the rail writes.
  const [colorRules, setColorRules] = useAtom(workspaceColorRulesAtom);

  // Values come off the workspace's own wells, so the picker offers what these wells
  // actually are rather than everything in the database — and needs no second fetch.
  const getColorOptions = useCallback(
    (categoryId: string) => workspaceColorOptions(wells, categoryId),
    [wells]
  );

  // The list half of the "Wells (map + list)" target. Resolved per row rather than once,
  // because a rule colours wells by their own values and every row is a different well.
  const colorForWell = useCallback(
    (well: StagedWell) =>
      resolveWellColor(colorRules, 'properties', {
        uwi: well.uwi,
        status: well.status,
        holeDirection: well.holeDirection,
        currentOperator: well.currentOperator,
        permitDate: well.permitDate,
        spudDate: well.spudDate,
        firstProductionDate: well.firstProductionDate,
        stateAbbreviation: well.stateAbbreviation,
        county: well.county,
        srpLandingZone: well.srpLandingZone,
        vendorLandingZone: well.vendorLandingZone,
        // A tree row is the well, not one of its traces, so a Wellbore Geometry rule
        // has nothing to say about it and is left unresolved here.
        [WELLBORE_GEOMETRY_ATTRIBUTE]: null,
      }),
    [colorRules]
  );

  const visibleCardCount = DETAIL_CARDS.filter((card) =>
    isCardVisible(visibleCards, card.id)
  ).length;

  return (
    <aside className={styles.rail}>
      <div
        className={styles.treePane}
        style={{ height: `${treeSplitPercent}%` }}
      >
        <WellTree
          wells={wells}
          selectedUwi={selectedUwi}
          onSelect={onSelectWell}
          onManageWells={onManageWells}
          manageWellsHint={manageWellsHint}
          colorFor={colorForWell}
        />
      </div>

      <DraggableDivider
        onDrag={setTreeSplitPercent}
        value={treeSplitPercent}
        label="Resize wells list and layout settings"
      />

      <div
        className={styles.controls}
        style={{ height: `${100 - treeSplitPercent}%` }}
      >
        <Accordion className={styles.accordion}>
          <AccordionItem
            open={open.wells}
            onHeadingClick={() => toggle('wells')}
            title={<RailSectionTitle label="Wells" count={colorRules.length} />}
          >
            <div className={styles.sectionBody}>
              <WellsColorBySection
                categories={WORKSPACE_COLOR_ATTRIBUTES}
                getOptions={getColorOptions}
                rules={colorRules}
                onChange={setColorRules}
                // Five, not the Well page's four: a workspace has a well list to paint.
                targets={WORKSPACE_COLOR_TARGETS}
              />
            </div>
          </AccordionItem>

          <AccordionItem
            open={open.layout}
            onHeadingClick={() => toggle('layout')}
            title={
              <RailSectionTitle
                label="Layout settings"
                count={visibleCardCount}
              />
            }
          >
            {/* One unnamed group: the workspace's rail lists its cards flat, so it
                gets no group heading and no Show all / Hide all master. */}
            <LayoutSettings
              groups={[{ items: DETAIL_CARDS }]}
              visible={visibleCards}
              idPrefix="toggle-"
              onToggle={onToggleCard}
            />
          </AccordionItem>
        </Accordion>
      </div>
    </aside>
  );
};
